import { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import './ZZPShared.css';
import './Functiegroepen.css';

function authFetch(url, options = {}) {
  const token = localStorage.getItem('ozel_token');
  const headers = {
    ...(options.headers || {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
  return fetch(url, { ...options, headers, credentials: 'include' });
}

const RATE_LABELS = [
  { key: 'bedrag_1', label: 'Bedrag 1 — Ochtenddienst (07:00-15:30)' },
  { key: 'bedrag_2', label: 'Bedrag 2 — Avonddienst (15:30-00:00)' },
  { key: 'bedrag_3', label: 'Bedrag 3 — Nachtdienst (00:00-08:00)' },
  { key: 'bedrag_4', label: 'Bedrag 4 — Overwerk na 8 uren' },
  { key: 'bedrag_5', label: 'Bedrag 5 — Zaterdag uren' },
  { key: 'bedrag_6', label: 'Bedrag 6 — Zondag uren' },
  { key: 'bedrag_7', label: 'Bedrag 7 — Feestdag uren' },
];

const EMPTY = {
  naam: '',
  bedrag_1: '',
  bedrag_2: '',
  bedrag_3: '',
  bedrag_4: '',
  bedrag_5: '',
  bedrag_6: '',
  bedrag_7: '',
  btw: false,
};

const fmtBedrag = (v) =>
  (v === null || v === undefined || v === '') ? '—' : `€ ${parseFloat(v).toFixed(2)}`;

export default function Functiegroepen() {
  const [items, setItems]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing]     = useState(null);
  const [form, setForm]           = useState(EMPTY);
  const [error, setError]         = useState('');
  const [saving, setSaving]       = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const url  = showInactive ? '/api/zzp/functiegroepen?all=1' : '/api/zzp/functiegroepen';
      const res  = await authFetch(url);
      const data = await res.json();
      setItems(Array.isArray(data) ? data : []);
    } catch {
      setItems([]);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, [showInactive]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setError('');
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      naam: row.naam || '',
      bedrag_1: row.bedrag_1 != null ? String(row.bedrag_1) : '',
      bedrag_2: row.bedrag_2 != null ? String(row.bedrag_2) : '',
      bedrag_3: row.bedrag_3 != null ? String(row.bedrag_3) : '',
      bedrag_4: row.bedrag_4 != null ? String(row.bedrag_4) : '',
      bedrag_5: row.bedrag_5 != null ? String(row.bedrag_5) : '',
      bedrag_6: row.bedrag_6 != null ? String(row.bedrag_6) : '',
      bedrag_7: row.bedrag_7 != null ? String(row.bedrag_7) : '',
      btw: !!row.btw,
    });
    setError('');
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
    setForm(EMPTY);
    setError('');
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((p) => ({ ...p, [name]: type === 'checkbox' ? checked : value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.naam.trim()) {
      setError('Naam is verplicht');
      return;
    }
    setSaving(true);
    const num = (v) => (v === '' || v === null || v === undefined ? null : parseFloat(v));
    const payload = {
      naam: form.naam.trim(),
      bedrag_1: num(form.bedrag_1),
      bedrag_2: num(form.bedrag_2),
      bedrag_3: num(form.bedrag_3),
      bedrag_4: num(form.bedrag_4),
      bedrag_5: num(form.bedrag_5),
      bedrag_6: num(form.bedrag_6),
      bedrag_7: num(form.bedrag_7),
      btw: form.btw,
    };
    try {
      const url    = editing ? `/api/zzp/functiegroepen/${editing.id}` : '/api/zzp/functiegroepen';
      const method = editing ? 'PUT' : 'POST';
      const res    = await authFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Opslaan mislukt');
      }
      await load();
      closeModal();
    } catch (err) {
      setError(err.message);
    }
    setSaving(false);
  };

  const handleDelete = async (row, e) => {
    e.stopPropagation();
    if (!confirm(`Functiegroep "${row.naam}" deactiveren?`)) return;
    try {
      const res = await authFetch(`/api/zzp/functiegroepen/${row.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Verwijderen mislukt');
      await load();
    } catch (err) {
      alert(err.message);
    }
  };

  return (
    <div className="zzp-page">
      <div className="zzp-header">
        <div>
          <h2>Functiegroepen</h2>
          <p>Beheer tariefgroepen die als template voor contracten dienen</p>
        </div>
        <button className="zzp-primary-btn" onClick={openCreate}>
          <Plus size={18} />
          Nieuwe functiegroep
        </button>
      </div>

      <div className="fg-toolbar">
        <label className="fg-toggle">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
          />
          Toon inactieve functiegroepen
        </label>
      </div>

      {loading ? (
        <div className="zzp-empty">Laden...</div>
      ) : items.length === 0 ? (
        <div className="zzp-empty">Nog geen functiegroepen. Klik op "Nieuwe functiegroep" om te beginnen.</div>
      ) : (
        <>
          {/* Mobile cards */}
          <div className="zzp-card-list">
            {items.map((row) => (
              <div key={row.id} className={`zzp-card${row.is_active === 0 ? ' fg-card-inactive' : ''}`} onClick={() => openEdit(row)}>
                <div className="zzp-card-top">
                  <span className="zzp-card-name">{row.naam}</span>
                  <div className="zzp-actions" onClick={(e) => e.stopPropagation()}>
                    <button className="zzp-icon-btn" onClick={() => openEdit(row)} title="Bewerken">
                      <Pencil size={14} />
                    </button>
                    {row.is_active !== 0 && (
                      <button className="zzp-icon-btn danger" onClick={(e) => handleDelete(row, e)} title="Deactiveren">
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </div>
                {RATE_LABELS.map(({ key, label }) => (
                  <div key={key} className="zzp-card-row">
                    <span className="zzp-card-label">{label.split('—')[0].trim()}</span>
                    {fmtBedrag(row[key])}
                  </div>
                ))}
                <div className="zzp-card-row">
                  <span className="zzp-card-label">Btw</span>
                  {row.btw ? '21%' : '—'}
                </div>
                {row.is_active === 0 && <div className="fg-inactive-badge">Inactief</div>}
              </div>
            ))}
          </div>

          {/* Desktop table */}
          <div className="zzp-table-wrap">
            <table className="zzp-table fg-table">
              <thead>
                <tr>
                  <th>Naam</th>
                  <th className="fg-rate-th">B1</th>
                  <th className="fg-rate-th">B2</th>
                  <th className="fg-rate-th">B3</th>
                  <th className="fg-rate-th">B4</th>
                  <th className="fg-rate-th">B5</th>
                  <th className="fg-rate-th">B6</th>
                  <th className="fg-rate-th">B7</th>
                  <th>Btw</th>
                  <th>Actief</th>
                  <th>Acties</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr key={row.id} onClick={() => openEdit(row)} className={`zzp-row${row.is_active === 0 ? ' fg-row-inactive' : ''}`}>
                    <td className="zzp-cell-strong">{row.naam}</td>
                    <td className="fg-rate-td">{fmtBedrag(row.bedrag_1)}</td>
                    <td className="fg-rate-td">{fmtBedrag(row.bedrag_2)}</td>
                    <td className="fg-rate-td">{fmtBedrag(row.bedrag_3)}</td>
                    <td className="fg-rate-td">{fmtBedrag(row.bedrag_4)}</td>
                    <td className="fg-rate-td">{fmtBedrag(row.bedrag_5)}</td>
                    <td className="fg-rate-td">{fmtBedrag(row.bedrag_6)}</td>
                    <td className="fg-rate-td">{fmtBedrag(row.bedrag_7)}</td>
                    <td>{row.btw ? '21%' : '—'}</td>
                    <td>{row.is_active === 0 ? '—' : 'Ja'}</td>
                    <td>
                      <div className="zzp-actions" onClick={(e) => e.stopPropagation()}>
                        <button className="zzp-icon-btn" onClick={() => openEdit(row)} title="Bewerken">
                          <Pencil size={14} />
                        </button>
                        {row.is_active !== 0 && (
                          <button className="zzp-icon-btn danger" onClick={(e) => handleDelete(row, e)} title="Deactiveren">
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {modalOpen && (
        <div className="zzp-modal-overlay" onClick={closeModal}>
          <div className="zzp-modal-content fg-modal" onClick={(e) => e.stopPropagation()}>
            <div className="zzp-modal-header">
              <h3>{editing ? 'Functiegroep bewerken' : 'Nieuwe functiegroep'}</h3>
              <button className="zzp-modal-close" onClick={closeModal}>✕</button>
            </div>

            <form onSubmit={handleSubmit} className="zzp-form">
              <div className="zzp-form-group">
                <label htmlFor="naam">Naam *</label>
                <input
                  id="naam"
                  type="text"
                  name="naam"
                  placeholder="Lasser klasse A"
                  value={form.naam}
                  onChange={handleChange}
                  autoFocus
                />
              </div>

              <div className="fg-rates-grid">
                {RATE_LABELS.map(({ key, label }) => (
                  <div key={key} className="zzp-form-group">
                    <label htmlFor={key}>{label}</label>
                    <input
                      id={key}
                      type="number"
                      name={key}
                      step="0.50"
                      min="0"
                      placeholder="0.00"
                      value={form[key]}
                      onChange={handleChange}
                    />
                  </div>
                ))}
                <label className="fg-toggle fg-btw-toggle">
                  <input
                    type="checkbox"
                    name="btw"
                    checked={form.btw}
                    onChange={handleChange}
                  />
                  Btw 21%
                </label>
              </div>

              {error && <div className="zzp-form-error">{error}</div>}

              <div className="zzp-form-actions">
                <button type="button" className="zzp-btn-cancel" onClick={closeModal}>
                  Annuleren
                </button>
                <button type="submit" className="zzp-btn-submit" disabled={saving}>
                  {saving ? 'Opslaan...' : (editing ? 'Opslaan' : 'Toevoegen')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
